import { useContext } from 'react';
import { AppCtx } from 'pages/_app';
import { findSubsidyTag } from '@lambda/reebelo/src/tagHelpers';
import { useVendorDispatch } from './use-vendor-details';
import { ItemTypeWithProps } from '@/components/cart/types';
import useDeliveryDate from './use-delivery-date';
import settings from '@/settings';
import { generateAddCartDateTimeAttr } from './use-cart';
import useOfferUppricerMeta from '@/lib/use-offer-uppricer-meta';

export default function useAttributes({ product, type }: ItemTypeWithProps) {
  const offerUppricerMeta = useOfferUppricerMeta({
    vendor: product.vendor,
    sku: type === 'offer' ? product.sku : undefined,
  });
  const offerUppricerMetaAttributes = [
    offerUppricerMeta?.upPricerRule != null && {
      key: '_uppricer',
      value: offerUppricerMeta.upPricerRule,
    },
    offerUppricerMeta?.upPriceAmount != null && {
      key: '_uppricer_total_amount',
      value: offerUppricerMeta.upPriceAmount.toString(),
    },
    offerUppricerMeta?.priceBeforeUpPricer != null && {
      key: '_uppricer_pre_upprice',
      value: offerUppricerMeta.priceBeforeUpPricer.toString(),
    },
    offerUppricerMeta?.priceAfterUpPricer != null && {
      key: '_uppricer_post_upprice',
      value: offerUppricerMeta.priceAfterUpPricer.toString(),
    },
  ].filter(Boolean) as { key: string; value: string }[];

  const vendorDispatchDetails = useVendorDispatch(product.vendor);
  const vendorAttributes = vendorDispatchDetails
    ? [
        { key: '_dispatch_min', value: vendorDispatchDetails.min.toString() },
        { key: '_dispatch_max', value: vendorDispatchDetails.max.toString() },
      ]
    : [];

  const subsidyTag =
    type === 'offer' ? findSubsidyTag(product.tags) : undefined;

  const subsidyAttributes =
    subsidyTag == null
      ? []
      : [
          {
            key: '_subsidy',
            value: subsidyTag,
          },
        ];

  const shippingAttributes = [
    {
      key: '_shipping_option_cost',
      value: '0',
    },
    {
      key: '_shipping_option',
      value: 'standard',
    },
  ];

  const { zipcodeStateData } = useContext(AppCtx);

  const deliveryDate = useDeliveryDate({
    zipcode: zipcodeStateData?.zipcode,
    state: zipcodeStateData?.state,
    category: type === 'offer' ? product.categories?.[0] : undefined,
    vendor: product.vendor,
  });

  const deliveryAttributes =
    deliveryDate == null ||
    deliveryDate.standard.minDeliveryDate === '' ||
    deliveryDate.standard.maxDeliveryDate === ''
      ? []
      : [
          ...(['reebelo-au', 'reebelo-nz', 'reebelo-ca'].includes(
            settings.store,
          )
            ? [
                {
                  key: '_deliver_zipcode',
                  value: zipcodeStateData?.zipcode || '',
                },
                {
                  key: '_deliver_state',
                  value: zipcodeStateData?.state || '',
                },
              ]
            : []),
          {
            key: '_deliver_min',
            value: deliveryDate.standard?.minDeliveryDate || '',
          },
          {
            key: '_deliver_max',
            value: deliveryDate.standard?.maxDeliveryDate || '',
          },
          {
            key: '_cutoff_time',
            value: deliveryDate.standard?.cutoffTime || '',
          },
          {
            key: '_cutoff_timezone',
            value: deliveryDate.standard?.cutoffTimezone || '',
          },
        ];

  const addCartDateTimeAttribute = generateAddCartDateTimeAttr();

  const categoryPkAttributes =
    type === 'offer' && product?.categoryPks?.[0] != null
      ? [
          {
            key: '_category_pk',
            value: product?.categoryPks?.[0] ?? '',
          },
        ]
      : [];

  return {
    addCartDateTimeAttribute,
    deliveryAttributes,
    shippingAttributes,
    subsidyAttributes,
    vendorAttributes,
    offerUppricerMetaAttributes,
    categoryPkAttributes,
  };
}
